import React from 'react';
import { Layout } from '../../components/layout/Layout';
import { MapPin, Shield, Users, FileCheck, AlertTriangle, Phone, Mail } from 'lucide-react';

const ModernSlaveryPage = () => {
    return (
        <Layout>
            <div className="min-h-screen bg-neutral-50">
                {/* Hero Section */}
                <div className="bg-gradient-to-r from-neutral-900 to-neutral-800 text-white py-16">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center gap-3 mb-4">
                            <Shield className="w-8 h-8 text-primary-400" />
                            <span className="text-primary-400 font-medium">Legal Statement</span>
                        </div>
                        <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
                            Modern Slavery & Human Trafficking Statement
                        </h1>
                        <p className="text-xl text-neutral-300">
                            RecruitNearMe — 2026
                        </p>
                    </div>
                </div>

                {/* Content */}
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 space-y-8">
                        
                        {/* Introduction */}
                        <section>
                            <h2 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                                    <FileCheck className="w-5 h-5 text-primary-600" />
                                </div>
                                Introduction
                            </h2>
                            <p className="text-neutral-600 leading-relaxed mb-4">
                                This statement is made pursuant to Section 54(1) of the Modern Slavery Act 2015 and constitutes RecruitNearMe's slavery and human trafficking statement for the financial year ending 2026.
                            </p>
                            <p className="text-neutral-600 leading-relaxed">
                                RecruitNearMe is committed to preventing slavery and human trafficking in our corporate activities and ensuring that our supply chains are free from slavery and human trafficking.
                            </p>
                        </section>

                        {/* Our Business */}
                        <section>
                            <h2 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                                    <MapPin className="w-5 h-5 text-primary-600" />
                                </div>
                                Our Business
                            </h2>
                            <p className="text-neutral-600 leading-relaxed mb-4">
                                RecruitNearMe operates as a recruitment agency directory and job application support platform, connecting job seekers with trusted recruitment agencies across the United Kingdom. Our services include:
                            </p>
                            <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4">
                                <li>Recruitment agency directory and search services</li>
                                <li>Multi-Apply job application services</li>
                                <li>Career consultation and CV review services</li>
                                <li>Job posting and recruitment advertising</li>
                            </ul>
                        </section>

                        {/* Our Policies */}
                        <section>
                            <h2 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                                    <Shield className="w-5 h-5 text-primary-600" />
                                </div>
                                Our Policies on Slavery and Human Trafficking
                            </h2>
                            <p className="text-neutral-600 leading-relaxed mb-4">
                                We are committed to ensuring that there is no modern slavery or human trafficking in our supply chains or in any part of our business. Our Anti-Slavery Policy reflects our commitment to acting ethically and with integrity in all our business relationships.
                            </p>
                            <div className="bg-neutral-50 rounded-xl p-6 space-y-3">
                                <h3 className="font-semibold text-neutral-900">Key Policy Commitments:</h3>
                                <ul className="space-y-2">
                                    {[
                                        'Zero tolerance approach to modern slavery',
                                        'Compliance with the Modern Slavery Act 2015',
                                        'Ethical recruitment practices throughout our operations',
                                        'Regular review and improvement of our policies',
                                        'Transparency in our supply chains'
                                    ].map((item, idx) => (
                                        <li key={idx} className="flex items-start gap-2 text-neutral-600">
                                            <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">✓</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </section>

                        {/* Due Diligence */}
                        <section>
                            <h2 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                                    <Users className="w-5 h-5 text-primary-600" />
                                </div>
                                Due Diligence Processes
                            </h2>
                            <p className="text-neutral-600 leading-relaxed mb-4">
                                As part of our initiative to identify and mitigate risk, we have implemented the following due diligence processes:
                            </p>
                            <div className="grid md:grid-cols-2 gap-4">
                                {[
                                    {
                                        title: 'Supplier Vetting',
                                        description: 'We conduct thorough background checks on all recruitment agencies listed on our platform.'
                                    },
                                    {
                                        title: 'Employee Training',
                                        description: 'Regular training for staff on identifying signs of modern slavery and human trafficking.'
                                    },
                                    {
                                        title: 'Risk Assessment',
                                        description: 'Ongoing assessment of modern slavery risks within our supply chain and operations.'
                                    },
                                    {
                                        title: 'Reporting Mechanisms',
                                        description: 'Clear channels for reporting concerns about slavery or human trafficking.'
                                    }
                                ].map((item, idx) => (
                                    <div key={idx} className="bg-neutral-50 rounded-lg p-4">
                                        <h4 className="font-semibold text-neutral-900 mb-2">{item.title}</h4>
                                        <p className="text-sm text-neutral-600">{item.description}</p>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Supply Chain */}
                        <section>
                            <h2 className="text-2xl font-bold text-neutral-900 mb-4">Our Supply Chains</h2>
                            <p className="text-neutral-600 leading-relaxed mb-4">
                                Our supply chains include:
                            </p>
                            <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4 mb-4">
                                <li>Recruitment agencies and employment businesses listed on our platform</li>
                                <li>Technology and software service providers</li>
                                <li>Payment processing services</li>
                                <li>Marketing and advertising partners</li>
                                <li>Professional service providers</li>
                            </ul>
                            <p className="text-neutral-600 leading-relaxed">
                                We expect the same high standards from all of our contractors, suppliers, and other business partners. As part of our contracting processes, we include specific prohibitions against the use of forced, compulsory, or trafficked labour.
                            </p>
                        </section>

                        {/* Reporting Concerns */}
                        <section>
                            <h2 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                                </div>
                                Reporting Concerns
                            </h2>
                            <p className="text-neutral-600 leading-relaxed mb-4">
                                We encourage anyone who has concerns about any aspect of our business or supply chain to report them to us. Reports can be made through our designated channels:
                            </p>
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                                <div className="space-y-3">
                                    <div className="flex items-center gap-3">
                                        <Mail className="w-5 h-5 text-amber-600" />
                                        <a href="mailto:compliance@recruitnearme.com" className="text-amber-800 hover:underline font-medium">
                                            compliance@recruitnearme.com
                                        </a>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Phone className="w-5 h-5 text-amber-600" />
                                        <span className="text-amber-800 font-medium">Modern Slavery Helpline: 08000 121 700</span>
                                    </div>
                                </div>
                                <p className="text-sm text-amber-700 mt-4">
                                    All reports will be treated with the utmost confidentiality and investigated thoroughly.
                                </p>
                            </div>
                        </section>

                        {/* Training */}
                        <section>
                            <h2 className="text-2xl font-bold text-neutral-900 mb-4">Training</h2>
                            <p className="text-neutral-600 leading-relaxed">
                                To ensure a high level of understanding of the risks of modern slavery and human trafficking in our supply chains and our business, we provide training to our staff. This training covers:
                            </p>
                            <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4 mt-4">
                                <li>The principles of the Modern Slavery Act 2015</li>
                                <li>How to identify potential victims of modern slavery</li>
                                <li>How to report concerns appropriately</li>
                                <li>Our purchasing practices and supplier requirements</li>
                            </ul>
                        </section>

                        {/* Effectiveness */}
                        <section>
                            <h2 className="text-2xl font-bold text-neutral-900 mb-4">Measuring Effectiveness</h2>
                            <p className="text-neutral-600 leading-relaxed mb-4">
                                We use the following key performance indicators (KPIs) to measure how effective we have been in ensuring that slavery and human trafficking is not taking place in any part of our business or supply chains:
                            </p>
                            <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4">
                                <li>Completion rates of modern slavery training among staff</li>
                                <li>Number of supplier audits conducted</li>
                                <li>Number of reports received through our reporting channels</li>
                                <li>Actions taken in response to any concerns raised</li>
                            </ul>
                        </section>

                        {/* Board Approval */}
                        <section className="border-t border-neutral-200 pt-8">
                            <h2 className="text-2xl font-bold text-neutral-900 mb-4">Board Approval</h2>
                            <p className="text-neutral-600 leading-relaxed mb-6">
                                This statement has been approved by the Board of Directors of RecruitNearMe and will be reviewed and updated annually.
                            </p>
                            <div className="bg-neutral-100 rounded-xl p-6">
                                <p className="text-neutral-600 mb-2">
                                    <strong>Signed:</strong> The Board of Directors
                                </p>
                                <p className="text-neutral-600 mb-2">
                                    <strong>Date:</strong> January 2026
                                </p>
                                <p className="text-neutral-600">
                                    <strong>Review Date:</strong> January 2027
                                </p>
                            </div>
                        </section>

                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default ModernSlaveryPage;
